import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Mail, MapPin, Phone } from 'lucide-react';

export default function TermsOfServicePage() {
  const lastUpdated = "March 2025";
  
  return (
    <div className="min-h-screen bg-cream pt-20" data-testid="terms-of-service-page">
      {/* Header */}
      <section className="py-12 px-6 bg-gradient-to-b from-charcoal to-charcoal/90">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <FileText className="mx-auto mb-4 text-gold" size={48} />
            <h1 className="font-serif text-4xl text-white mb-4">Terms of Service</h1>
            <p className="text-white/70">Last updated: {lastUpdated}</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border border-gold/10 prose prose-lg max-w-none">
            
            <h2 className="font-serif text-2xl text-charcoal mt-0">Agreement to Terms</h2>
            <p className="text-charcoal/80">
              By accessing or using the Living Fruit Ministry website (livingfruitministry.org), 
              you agree to be bound by these Terms of Service. If you do not agree to these terms, 
              please do not use our website or services.
            </p>

            <h2 className="font-serif text-2xl text-charcoal">About Living Fruit Ministry</h2>
            <p className="text-charcoal/80">
              Living Fruit Ministry is a 501(c)(3) nonprofit religious organization based in 
              Kissimmee, Florida. We provide spiritual guidance, community support, food assistance, 
              and various ministry programs to serve Central Florida and beyond.
            </p>

            <h2 className="font-serif text-2xl text-charcoal">Use of Our Services</h2>
            
            <h3 className="font-serif text-xl text-charcoal">Permitted Use</h3>
            <p className="text-charcoal/80">You may use our website and services for:</p>
            <ul className="text-charcoal/80">
              <li>Learning about our ministry and programs</li>
              <li>Submitting prayer requests</li>
              <li>Registering for events and volunteer opportunities</li>
              <li>Making donations</li>
              <li>Accessing community resources</li>
              <li>Contacting our ministry team</li>
            </ul>

            <h3 className="font-serif text-xl text-charcoal">Prohibited Use</h3>
            <p className="text-charcoal/80">You agree not to:</p>
            <ul className="text-charcoal/80">
              <li>Use our services for any illegal purpose</li>
              <li>Submit false or misleading information</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with the proper functioning of our website</li>
              <li>Use automated systems to access our services without permission</li>
              <li>Harass, threaten, or harm other users or staff</li>
              <li>Post content that is offensive, defamatory, or inappropriate</li>
            </ul>

            <h2 className="font-serif text-2xl text-charcoal">User Submissions</h2>
            <p className="text-charcoal/80">
              When you submit content to our website (prayer requests, testimonies, business listings, 
              feedback, etc.), you grant us permission to use, display, and share this content as part 
              of our ministry activities. You retain ownership of your content but give us a license 
              to use it for ministry purposes.
            </p>
            <p className="text-charcoal/80">
              We reserve the right to review, edit, or remove any user-submitted content at our 
              discretion, particularly content that violates these terms or our community guidelines.
            </p>

            <h2 className="font-serif text-2xl text-charcoal">Donations</h2>
            <p className="text-charcoal/80">
              All donations to Living Fruit Ministry are tax-deductible to the extent allowed by law. 
              We are a registered 501(c)(3) nonprofit organization. Donations are processed through 
              secure third-party payment processors. By making a donation, you agree to the payment 
              processor's terms of service.
            </p>
            <p className="text-charcoal/80">
              We strive to use donations in accordance with donor intent. However, we reserve the 
              right to redirect funds to where they are most needed if circumstances require.
            </p>

            <h2 className="font-serif text-2xl text-charcoal">Intellectual Property</h2>
            <p className="text-charcoal/80">
              The content on our website, including text, graphics, logos, images, and software, 
              is owned by Living Fruit Ministry or our licensors and is protected by copyright and 
              other intellectual property laws. You may not reproduce, distribute, or create 
              derivative works without our written permission.
            </p>

            <h2 className="font-serif text-2xl text-charcoal">Third-Party Links</h2>
            <p className="text-charcoal/80">
              Our website may contain links to third-party websites and services, including 
              social media platforms, payment processors, and partner organizations. We are not 
              responsible for the content or practices of these third parties. Your use of 
              third-party services is at your own risk.
            </p>

            <h2 className="font-serif text-2xl text-charcoal">Disclaimer</h2>
            <p className="text-charcoal/80">
              Our website and services are provided "as is" without warranties of any kind, 
              either express or implied. We do not warrant that our services will be uninterrupted, 
              error-free, or secure. Spiritual guidance and resources are provided for general 
              support and should not replace professional counseling, medical, or legal advice.
            </p>

            <h2 className="font-serif text-2xl text-charcoal">Limitation of Liability</h2>
            <p className="text-charcoal/80">
              To the fullest extent permitted by law, Living Fruit Ministry shall not be liable 
              for any indirect, incidental, special, consequential, or punitive damages arising 
              from your use of our website or services.
            </p>

            <h2 className="font-serif text-2xl text-charcoal">Indemnification</h2>
            <p className="text-charcoal/80">
              You agree to indemnify and hold harmless Living Fruit Ministry, its officers, 
              directors, employees, and volunteers from any claims, damages, or expenses arising 
              from your violation of these terms or your use of our services.
            </p>

            <h2 className="font-serif text-2xl text-charcoal">Changes to Terms</h2>
            <p className="text-charcoal/80">
              We may update these Terms of Service from time to time. We will notify you of 
              significant changes by posting a notice on our website. Your continued use of our 
              services after changes are posted constitutes acceptance of the updated terms.
            </p>

            <h2 className="font-serif text-2xl text-charcoal">Governing Law</h2>
            <p className="text-charcoal/80">
              These Terms of Service are governed by the laws of the State of Florida, without 
              regard to conflict of law principles. Any disputes shall be resolved in the courts 
              of Osceola County, Florida.
            </p>

            <h2 className="font-serif text-2xl text-charcoal">Contact Us</h2>
            <p className="text-charcoal/80">
              If you have questions about these Terms of Service, please contact us:
            </p>
            <div className="bg-cream rounded-xl p-6 not-prose">
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-charcoal/80">
                  <Mail size={18} className="text-gold" />
                  <span>livingfruitministry@gmail.com</span>
                </div>
                <div className="flex items-center gap-3 text-charcoal/80">
                  <Phone size={18} className="text-gold" />
                  <span>(407) 300-6299</span>
                </div>
                <div className="flex items-center gap-3 text-charcoal/80">
                  <MapPin size={18} className="text-gold" />
                  <span>109 N Beaumont Avenue #1078, Kissimmee, FL 34741</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
